import { getWatermarkFontFamily } from './watermarkFont';

const WATERMARK_EDITOR_WIDTH = 720;
const WATERMARK_POSITIONS = [
  'top-left', 'top', 'top-right',
  'left', 'center', 'right',
  'bottom-left', 'bottom', 'bottom-right',
];

function clampInteger(value, minimum, maximum, fallback) {
  if (value == null || value === '') return fallback;
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.min(maximum, Math.max(minimum, Math.round(number)));
}

function normalizeWatermarkStyle(value) {
  return value === 'fill' ? 'fill' : 'single';
}

function normalizeWatermarkPosition(value) {
  return WATERMARK_POSITIONS.includes(value) ? value : 'bottom-right';
}

function getDefaultWatermarkRotation() {
  return 0;
}

function normalizeWatermarkEditorConfig(value = {}) {
  const style = normalizeWatermarkStyle(value.style);
  const alpha = value.alpha == null || value.alpha === '' ? NaN : Number(value.alpha);
  const rotate = clampInteger(value.rotate, -45, 45, getDefaultWatermarkRotation(style));
  return {
    ...value,
    enabled: Boolean(value.enabled),
    text: String(value.text ?? ''),
    style,
    pos: normalizeWatermarkPosition(value.pos),
    size: clampInteger(value.size, 12, 100, 36),
    alpha: Number.isFinite(alpha) ? Math.min(1, Math.max(0.05, alpha)) : 0.5,
    shadow: Boolean(value.shadow) && rotate === 0,
    inset: clampInteger(value.inset, 0, 80, 24),
    rotate,
    weight: 400,
  };
}

function getWatermarkPreviewMetrics(value, outputWidth = WATERMARK_EDITOR_WIDTH) {
  const wm = normalizeWatermarkEditorConfig(value);
  const width = clampInteger(outputWidth, 1, 16384, WATERMARK_EDITOR_WIDTH);
  return {
    ...wm,
    outputWidth: width,
    fontSize: clampInteger(wm.size * width / WATERMARK_EDITOR_WIDTH, 1, 1000, wm.size),
    inset: clampInteger(wm.inset * width / WATERMARK_EDITOR_WIDTH, 0, 4096, 0),
    // The server chooses its OSS font from the unscaled editor size too.
    fontFamily: getWatermarkFontFamily(wm.size),
    color: '#FFFFFF',
    shadowColor: 'rgba(0,0,0,.5)',
    shadowBlur: 8,
    shadowOffsetX: 0,
    shadowOffsetY: 1,
  };
}

function getFillWatermarkSpacing(textWidth, fontSize) {
  return {
    stepX: Number(textWidth) + Number(fontSize) * 3,
    stepY: Number(fontSize) * 4,
  };
}

function getRotatedBounds(width, height, rotation) {
  const radians = Number(rotation || 0) * Math.PI / 180;
  const cosine = Math.abs(Math.cos(radians));
  const sine = Math.abs(Math.sin(radians));
  return {
    width: width * cosine + height * sine,
    height: width * sine + height * cosine,
  };
}

function getSingleWatermarkCenter({
  position,
  canvasWidth,
  canvasHeight,
  textWidth,
  textHeight,
  inset,
  rotation,
}) {
  const normalizedPosition = normalizeWatermarkPosition(position);
  const bounds = getRotatedBounds(textWidth, textHeight, rotation);
  let vertical = 'center';
  let horizontal = 'center';
  if (normalizedPosition.includes('-')) {
    [vertical, horizontal] = normalizedPosition.split('-');
  } else if (normalizedPosition === 'top' || normalizedPosition === 'bottom') {
    vertical = normalizedPosition;
  } else if (normalizedPosition === 'left' || normalizedPosition === 'right') {
    horizontal = normalizedPosition;
  }
  const x = horizontal === 'left'
    ? inset + bounds.width / 2
    : horizontal === 'right'
      ? canvasWidth - inset - bounds.width / 2
      : canvasWidth / 2;
  const y = vertical === 'top'
    ? inset + bounds.height / 2
    : vertical === 'bottom'
      ? canvasHeight - inset - bounds.height / 2
      : canvasHeight / 2;
  return { x, y, bounds };
}

export {
  WATERMARK_EDITOR_WIDTH,
  WATERMARK_POSITIONS,
  getDefaultWatermarkRotation,
  getFillWatermarkSpacing,
  getRotatedBounds,
  getSingleWatermarkCenter,
  getWatermarkPreviewMetrics,
  normalizeWatermarkEditorConfig,
  normalizeWatermarkPosition,
  normalizeWatermarkStyle,
};
